import { useState, useEffect, useCallback, useMemo } from 'react';
import { clientAPI } from '@/services/api';
import { useDebounce } from './useDebounce';

export const useClientBookings = (filters = {}) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });

  // Stabiliser les filtres pour éviter les boucles infinies
  const stableFilters = useMemo(() => filters, [
    filters.search,
    filters.status,
    filters.status_code,
    filters.date_range,
  ]);

  // Debouncer les filtres pour éviter les requêtes trop fréquentes
  const debouncedFilters = useDebounce(stableFilters, 300);

  const fetchBookings = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      // Filtrer les paramètres vides ou "all"
      const cleanFilters = Object.fromEntries(
        Object.entries(debouncedFilters).filter(([key, value]) => 
          value !== '' && value !== 'all' && value != null
        )
      );
      
      const params = {
        page,
        ...cleanFilters,
      };
      
      const response = await clientAPI.getBookings(params);
      setBookings(response.data.results || response.data);
      setPagination({
        count: response.data.count || 0,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (err) {
      console.error('Erreur lors du chargement des réservations:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des réservations');
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);

  const getBookingById = useCallback(async (bookingId) => {
    try {
      const response = await clientAPI.getBookingById(bookingId);
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors du chargement de la réservation:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors du chargement de la réservation' 
      };
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  return {
    bookings,
    loading,
    error,
    pagination,
    fetchBookings,
    getBookingById,
  };
};

